import React from 'react';
import { useCurrentRole } from '../components/demo/RoleSwitcher';
import { PortfolioDashboard } from '../components/captive-manager/PortfolioDashboard';
import { Dashboard as MGADashboard } from '../components/steps/Dashboard';
import { ExecutiveSummary } from '../components/dashboards/ExecutiveSummary';
import { FinancialPerformance } from '../components/dashboards/FinancialPerformance';
import { LossPerformanceRatios } from '../components/dashboards/LossPerformanceRatios';
import { CapitalReturnOnCapital } from '../components/dashboards/CapitalReturnOnCapital';
import { ReinsuranceRetrocession } from '../components/dashboards/ReinsuranceRetrocession';
import { ExposureAccumulation } from '../components/dashboards/ExposureAccumulation';
import { PortfolioMixGrowth } from '../components/dashboards/PortfolioMixGrowth';
import { CatastropheRisk } from '../components/dashboards/CatastropheRisk';
import { CreditControlOperations } from '../components/dashboards/CreditControlOperations';
import { Calendar } from '../components/dashboards/Calendar';
import { Compliance } from '../components/dashboards/Compliance';
// Captive Manager Dashboards
import { CaptivePortfolioOverview } from './captive-manager/CaptivePortfolioOverview';
import { CaptiveCellPerformance } from './captive-manager/CaptiveCellPerformance';
import { CaptiveUnderwritingPricing } from './captive-manager/CaptiveUnderwritingPricing';
import { CaptiveClaimsGovernance } from './captive-manager/CaptiveClaimsGovernance';
import { CaptiveCapitalReinsurance } from './captive-manager/CaptiveCapitalReinsurance';
import { GovernanceEvidenceAgent } from './captive-manager/GovernanceEvidenceAgent';
// Admin Dashboard placeholder
function AdminDashboard() {
  return <div className="space-y-6" data-id="element-2210">
      <div className="bg-white rounded-lg shadow p-6" data-id="element-2211">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2212">
          Admin Dashboard
        </h1>
        <p className="text-gray-600" data-id="element-2213">
          System administration and platform management
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6" data-id="element-2214">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-600" data-id="element-2215">
          <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-2216">
            Total Users
          </h3>
          <p className="text-3xl font-bold text-gray-900" data-id="element-2217">127</p>
          <p className="text-sm text-gray-600 mt-1" data-id="element-2218">43 active today</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-600" data-id="element-2219">
          <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-2220">Total MGAs</h3>
          <p className="text-3xl font-bold text-gray-900" data-id="element-2221">18</p>
          <p className="text-sm text-gray-600 mt-1" data-id="element-2222">5 onboarding</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-600" data-id="element-2223">
          <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-2224">
            Storage Used
          </h3>
          <p className="text-3xl font-bold text-gray-900" data-id="element-2225">145 GB</p>
          <p className="text-sm text-gray-600 mt-1" data-id="element-2226">of 500 GB</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6" data-id="element-2227">
        <h2 className="text-lg font-semibold mb-4" data-id="element-2228">Quick Actions</h2>
        <div className="grid md:grid-cols-2 gap-4" data-id="element-2229">
          <button className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-600 hover:bg-blue-50 transition-colors text-left" data-id="element-2230">
            <h3 className="font-semibold text-gray-900 mb-1" data-id="element-2231">
              User Management
            </h3>
            <p className="text-sm text-gray-600" data-id="element-2232">
              Create, edit, or delete user accounts
            </p>
          </button>
          <button className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-600 hover:bg-blue-50 transition-colors text-left" data-id="element-2233">
            <h3 className="font-semibold text-gray-900 mb-1" data-id="element-2234">
              System Settings
            </h3>
            <p className="text-sm text-gray-600" data-id="element-2235">
              Configure platform-wide settings
            </p>
          </button>
          <button className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-600 hover:bg-blue-50 transition-colors text-left" data-id="element-2236">
            <h3 className="font-semibold text-gray-900 mb-1" data-id="element-2237">Audit Logs</h3>
            <p className="text-sm text-gray-600" data-id="element-2238">
              View system activity and changes
            </p>
          </button>
          <button className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-600 hover:bg-blue-50 transition-colors text-left" data-id="element-2239">
            <h3 className="font-semibold text-gray-900 mb-1" data-id="element-2240">Reports</h3>
            <p className="text-sm text-gray-600" data-id="element-2241">
              Generate platform analytics reports
            </p>
          </button>
        </div>
      </div>
    </div>;
}
interface RoleBasedDashboardProps {
  selectedDashboard?: string;
  onNavigate?: (dashboard: string) => void;
}
// Role-based dashboard router
export function RoleBasedDashboard({
  selectedDashboard = 'Executive Summary',
  onNavigate
}: RoleBasedDashboardProps) {
  const role = useCurrentRole();
  // For Captive Manager role
  if (role === 'captive_manager') {
    switch (selectedDashboard) {
      case 'Portfolio Overview':
        return <CaptivePortfolioOverview onNavigate={onNavigate} data-id="element-2242" />;
      case 'Cell Performance':
        return <CaptiveCellPerformance onNavigate={onNavigate} data-id="element-2243" />;
      case 'Underwriting & Pricing':
        return <CaptiveUnderwritingPricing onNavigate={onNavigate} data-id="element-2244" />;
      case 'Claims & Governance':
        return <CaptiveClaimsGovernance onNavigate={onNavigate} data-id="element-2245" />;
      case 'Capital & Reinsurance':
        return <CaptiveCapitalReinsurance onNavigate={onNavigate} data-id="element-2246" />;
      case 'Governance Agent':
        return <GovernanceEvidenceAgent onNavigate={onNavigate} data-id="element-2247" />;
      default:
        return <CaptivePortfolioOverview onNavigate={onNavigate} data-id="element-2248" />;
    }
  }
  // For Platform Admin role
  if (role === 'platform_admin') {
    return <AdminDashboard data-id="element-2249" />;
  }
  // For MGA User role - show selected dashboard
  switch (selectedDashboard) {
    case 'Executive Summary':
      return <ExecutiveSummary data-id="element-2250" />;
    case 'Financial Performance':
      return <FinancialPerformance data-id="element-2251" />;
    case 'Loss Performance & Ratios':
      return <LossPerformanceRatios data-id="element-2252" />;
    case 'Capital & Return on Capital':
      return <CapitalReturnOnCapital data-id="element-2253" />;
    case 'Reinsurance & Retrocession':
      return <ReinsuranceRetrocession data-id="element-2254" />;
    case 'Exposure, Line Size & Accumulation':
      return <ExposureAccumulation data-id="element-2255" />;
    case 'Portfolio Mix & Growth':
      return <PortfolioMixGrowth data-id="element-2256" />;
    case 'Catastrophe Risk':
      return <CatastropheRisk data-id="element-2257" />;
    case 'Credit Control & Operations':
      return <CreditControlOperations data-id="element-2258" />;
    case 'Calendar':
      return <Calendar data-id="element-2259" />;
    case 'Compliance':
      return <Compliance data-id="element-2260" />;
    default:
      return <MGADashboard data-id="element-2261" />;
  }
}